import { ReactNode } from 'react';
import { AlertCircle, Info, CheckCircle, AlertTriangle } from 'lucide-react';

interface AlertProps {
  children: ReactNode;
  type?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
}

const alertStyles = {
  info: {
    container: 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800',
    icon: 'text-blue-600 dark:text-blue-400',
    title: 'text-blue-900 dark:text-blue-100',
    text: 'text-blue-800 dark:text-blue-200',
    Icon: Info,
  },
  success: {
    container: 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800',
    icon: 'text-green-600 dark:text-green-400',
    title: 'text-green-900 dark:text-green-100',
    text: 'text-green-800 dark:text-green-200',
    Icon: CheckCircle,
  },
  warning: {
    container: 'bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800',
    icon: 'text-yellow-600 dark:text-yellow-400',
    title: 'text-yellow-900 dark:text-yellow-100',
    text: 'text-yellow-800 dark:text-yellow-200',
    Icon: AlertTriangle,
  },
  error: {
    container: 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800',
    icon: 'text-red-600 dark:text-red-400',
    title: 'text-red-900 dark:text-red-100',
    text: 'text-red-800 dark:text-red-200',
    Icon: AlertCircle,
  },
};

export function Alert({ children, type = 'info', title }: AlertProps) {
  const validType = type in alertStyles ? type : 'info';
  const styles = alertStyles[validType];
  const Icon = styles.Icon;

  return (
    <div className={`my-6 rounded-lg border p-4 ${styles.container}`}>
      <div className="flex gap-3">
        <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${styles.icon}`} />
        <div className="flex-1">
          {title && (
            <h4 className={`font-semibold mb-1 ${styles.title}`}>
              {title}
            </h4>
          )}
          <div className={`text-sm ${styles.text} [&>*:last-child]:mb-0`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
